import { modalActions } from '@/actions/modalActions';
import { trackServiceDetailsViewed } from '@/features/booking-flow/tracking';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { BookState } from '@/types/state/book';
import { ReviewsModalType } from '@/types/modal';
import { createContext, lazy, ReactNode, Suspense, useCallback, useContext, useEffect, useRef, useState } from 'react';

// Lazy: the reviews modal (and its router-bound review cards) loads only once a service's details open.
const ReviewsModal = lazy(() =>
  import('@/features/reviews/ReviewsModal').then((module) => ({ default: module.ReviewsModal })),
);

type ServiceDetailsCta = { label: string; onPick: () => void };

type OpenServiceDetails = (serviceId: number, cta: ServiceDetailsCta) => void;

const ServiceDetailsContext = createContext<OpenServiceDetails | undefined>(undefined);

/** Undefined outside a ServiceDetailsProvider, or while the reviews modal isn't available. */
export const useOpenServiceDetails = () => useContext(ServiceDetailsContext);

/**
 * Lets booking-flow service rows open the shared ReviewsModal on a service, with the footer
 * CTA picking that service in place instead of redirecting to the calendar.
 */
export const ServiceDetailsProvider = ({
  children,
  trackServiceDetails = true,
}: {
  children: ReactNode;
  /**
   * Off on the calendar: viewServiceDetails is outside U15 there and the legacy rows never emit
   * it, so emitting from here would count opens for the listcards-v2 arm only.
   */
  trackServiceDetails?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const place = useAppSelector((state) => (state.book as BookState | undefined)?.place);
  const placeId = place?.id;
  // Mirrors PlacePage's hideStaff.
  const hideStaff = Boolean(place?.schedule) || Boolean(place?.about?.settings?.hideEmployees);
  const isModalShown = useAppSelector((state) =>
    Boolean((state.modal as { reviews?: ReviewsModalType } | undefined)?.reviews?.show),
  );
  const [opened, setOpened] = useState<{ serviceId: number; cta: ServiceDetailsCta }>();

  const isOpenRef = useRef(false);
  isOpenRef.current = Boolean(opened);

  const closeDetails = () => {
    setOpened(undefined);
    if (trackServiceDetails) trackServiceDetailsViewed('cancel');
  };

  // The modal closes itself (X, backdrop); this is where the provider learns about it.
  useEffect(() => {
    if (opened && !isModalShown) closeDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, isModalShown]);

  // A pick that leaves the step unmounts the provider with the modal still flagged open in redux.
  useEffect(
    () => () => {
      if (isOpenRef.current) dispatch(modalActions.reviews({ show: false, context: undefined }));
    },
    [dispatch],
  );

  const openServiceDetails = useCallback<OpenServiceDetails>(
    (serviceId, cta) => {
      setOpened({ serviceId, cta });
      if (trackServiceDetails) trackServiceDetailsViewed('initiated');
      // The reducer merges, so clear filters a previous open may have left behind.
      dispatch(
        modalActions.reviews({
          show: true,
          placeId,
          focus: 'service',
          context: 'bookingFlow',
          serviceId,
          employeeId: undefined,
          rating: undefined,
        }),
      );
    },
    [dispatch, placeId, trackServiceDetails],
  );

  // The CTA belongs to the service the row opened; one reached by navigating inside the modal
  // keeps the modal's default redirect.
  const getServiceCta = (serviceId: number) =>
    opened && serviceId === opened.serviceId
      ? {
          label: opened.cta.label,
          onClick: () => {
            // Pick first, then close — the modal arm's order (onPickService, then the close
            // that fires viewServiceDetails('cancel')).
            opened.cta.onPick();
            closeDetails();
          },
        }
      : undefined;

  return (
    <ServiceDetailsContext.Provider value={placeId ? openServiceDetails : undefined}>
      {children}
      {opened ? (
        <Suspense fallback={null}>
          <ReviewsModal placeId={placeId} hideStaff={hideStaff} getServiceCta={getServiceCta} />
        </Suspense>
      ) : null}
    </ServiceDetailsContext.Provider>
  );
};
